import React, { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import api from "@/services/api";
import { API_ENDPOINTS } from "@/config/api";
import FancySettlementModal from "@/components/FancySettlementModal";

const LIMIT = 100;
const DEFAULT_OFFSET = 0;

const MARKETS_FOR_FANCY = [
  "1st Innings 6 Overs Line",
  "2nd Innings 6 Overs Line",
  "3rd Innings 6 Overs Line",
  "1st Innings 50 Overs Line",
  "2nd Innings 50 Overs Line",
  "3rd Innings 50 Overs Line",
  "1st Innings 40 Overs Line",
  "2nd Innings 40 Overs Line",
  "3rd Innings 40 Overs Line",
  "1st Innings 30 Overs Line",
  "2nd Innings 30 Overs Line",
  "3rd Innings 30 Overs Line",
  "1st Innings 20 Overs Line",
  "2nd Innings 20 Overs Line",
  "3rd Innings 20 Overs Line",
  "1st Innings 10 Overs Line",
  "2nd Innings 10 Overs Line",
  "3rd Innings 10 Overs Line",
  "Over By Over",
  "Ball By Ball",
  "Normal",
  "khado",
  "meter",
  "fancy1",
  "oddeven",
];

function getOrderedTabKeys(data) {
  if (!data || typeof data !== "object") return [];
  const keysWithData = Object.keys(data).filter(
    (k) => Array.isArray(data[k]) && data[k].length > 0
  );
  const ordered = [];
  for (const name of MARKETS_FOR_FANCY) {
    if (keysWithData.includes(name)) ordered.push(name);
  }
  if (keysWithData.includes("others")) ordered.push("others");
  for (const k of keysWithData) {
    if (!ordered.includes(k)) ordered.push(k);
  }
  return ordered;
}

export default function FancySettlement() {
  const [fancyData, setFancyData] = useState(null);
  const [tabKeys, setTabKeys] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [modalMode, setModalMode] = useState("declare");
  const [selectedRow, setSelectedRow] = useState(null);

  const fetchFanmatches = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await api.get(API_ENDPOINTS.INTERNALSETTLE.FANMATCHES, {
        params: { limit: LIMIT, offset: DEFAULT_OFFSET },
      });
      if (response.data?.success && response.data.data && typeof response.data.data === "object") {
        const data = response.data.data;
        setFancyData(data);
        const ordered = getOrderedTabKeys(data);
        setTabKeys(ordered);
        setActiveTab((prev) => (ordered.length > 0 && !ordered.includes(prev) ? ordered[0] : prev));
      } else {
        setFancyData({});
        setTabKeys([]);
      }
    } catch (error) {
      if (!silent) console.error("Error fetching fancy matches:", error);
      setFancyData({});
      setTabKeys([]);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchFanmatches();
    const interval = setInterval(() => fetchFanmatches(true), 2000);
    return () => clearInterval(interval);
  }, []);

  const rows = (activeTab && fancyData && Array.isArray(fancyData[activeTab]))
    ? fancyData[activeTab]
    : [];

  const handleDeclare = (row) => {
    setSelectedRow({ ...row, marketType: row.marketType || activeTab });
    setModalMode("declare");
    setModalShow(true);
  };

  const handleVoid = (row) => {
    setSelectedRow({ ...row, marketType: row.marketType || activeTab });
    setModalMode("void");
    setModalShow(true);
  };

  const handleSubmit = async (payload) => {
    console.log("Fancy settlement submit", payload);
    setModalShow(false);
    setSelectedRow(null);
  };

  return (
    <Layout>
      <div className="p-4 w-full mx-auto bg-[#171b36] min-h-screen">
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
          className="shadow-sm px-4 pt-4"
        >
          <div className="flex justify-between items-center mb-1">
            <div
              style={{ color: "#cae0e8", fontSize: "24px" }}
              className="font-semibold"
            >
              Fancy Settlement
            </div>
            <button
              type="button"
              onClick={() => fetchFanmatches()}
              disabled={loading}
              className="px-4 py-2 bg-[#11374a] text-white text-[14px] font-bold hover:bg-[#1a4f66] transition-colors disabled:opacity-50"
            >
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
          <div className="w-full h-px bg-white mb-2"></div>
        </div>

        {/* Tabs from API keys - flex wrap */}
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)", marginTop: "-8px" }}
          className="shadow-sm px-4 pt-4 pb-2"
        >
          <div className="flex flex-wrap gap-2 mb-4">
            {tabKeys.map((key) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`px-4 py-2 text-[12px] font-bold transition-colors ${activeTab === key
                  ? "bg-[#333d79ff] text-white"
                  : "bg-gray-600 text-white"
                  }`}
              >
                {key}
              </button>
            ))}
          </div>
        </div>

        {/* Table - same style as Match Settlement */}
        <div
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            marginTop: "-8px",
            minHeight: "500px",
          }}
          className="shadow-sm p-4"
        >
          <div className="flex-1 min-w-0">
            <div className="overflow-hidden">
              <table className="w-full text-sm table-fixed">
                <thead>
                  <tr>
                    <th className="text-left px-4 py-2 text-white font-bold text-[15px] w-[25%]">
                      Match Title
                    </th>
                    <th className="text-left px-4 py-2 text-white font-bold text-[15px] w-[25%]">
                      {MARKETS_FOR_FANCY.includes(activeTab) ? "Market Name" : "Market Type"}
                    </th>
                    <th className="text-right px-4 py-2 text-white font-semibold w-[12%]">
                      Total Bets
                    </th>
                    <th className="text-center px-4 py-2 text-white font-bold text-[15px] w-[20%]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={4} className="text-center text-white py-4">
                        Loading...
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center text-white py-4">
                        No matches found
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, index) => (
                      <tr
                        key={`${row.matchId}-${row.eventId}-${index}`}
                        className={`transition-colors ${index % 2 === 0 ? "bg-[rgba(245,245,245,0.1)]" : ""
                          }`}
                      >
                        <td className="py-2 px-4 text-[#2ce5dd] font-medium truncate">
                          {row.eventName || row.matchTitle || "-"}
                        </td>
                        <td className="py-2 px-4 text-white font-bold truncate">
                          {MARKETS_FOR_FANCY.includes(activeTab) ? (row.selectionName || "-") : (row.marketType ?? activeTab ?? "-")}
                        </td>
                        <td className="py-2 px-4 text-right text-white font-bold">
                          {row.totalBets ?? 0}
                        </td>
                        <td className="py-2 px-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleDeclare(row)}
                              className="px-3 py-1 bg-[#11374a] text-white text-[12px] font-bold hover:bg-[#1a4f66] transition-colors"
                            >
                              Declare
                            </button>
                            <button
                              onClick={() => handleVoid(row)}
                              className="px-3 py-1 bg-gray-600 text-white text-[12px] font-bold hover:bg-gray-500 transition-colors"
                            >
                              Void
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <FancySettlementModal
        show={modalShow}
        onHide={() => {
          setModalShow(false);
          setSelectedRow(null);
        }}
        mode={modalMode}
        row={selectedRow}
        onSubmit={handleSubmit}
      />
    </Layout>
  );
}
